import { api } from 'api/api';
import { Endpoints } from 'api/constants';
import { IAdmin, IBaseAdmin } from 'types/income';
import { AppLanguage, TableDataResponse } from 'types/general';
import { getSorting } from 'utils/general';
import { RoleType } from 'store/permissions';
import { AdminsFilters, AdminStatus } from './admins.types';

const getRoleFilters = (roleIds: string[] = []) => {
  const adminRole = new Set<RoleType>();
  const customRoleIds: string[] = [];

  roleIds.forEach((roleId) => {
    if (roleId === 'admin' || roleId === 'superAdmin') {
      adminRole.add(roleId);
    } else {
      adminRole.add('custom');
      customRoleIds.push(roleId);
    }
  });

  return {
    adminRole: adminRole.size ? Array.from(adminRole) : undefined,
    customRoleIds: customRoleIds.length ? customRoleIds : undefined,
  };
};

interface GetAdmins {
  filters: Partial<AdminsFilters>;
}

interface GetAdmin {
  adminId: string;
}

interface CreateAdmin {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roleType: RoleType;
  customRoleId?: string;
  roleScopeCompanyIds?: string[];
}

interface CreateProgrammaticAdmin {
  firstName: string;
  roleType: RoleType;
  customRoleId?: string;
  roleScopeCompanyIds?: string[];
}

interface UpdateAdmin {
  adminId: string;
  data: Partial<
    CreateAdmin & {
      status: AdminStatus;
      language: Uppercase<AppLanguage>;
      employeeId: string | null;
    }
  >;
}

interface UpdateMe {
  data: Partial<{
    firstName: string;
    lastName: string;
    language: Uppercase<AppLanguage>;
  }>;
}

interface SendInvite {
  adminId: string;
}

interface DeleteAdmin {
  adminId: string;
}

interface CreateAdminToken {
  adminId: string;
  data: {
    name: string;
  };
}

interface DeleteAdminToken {
  adminId: string;
  jti: string;
}

export const loaders = {
  getAdmins: ({ filters }: GetAdmins) => {
    return api<TableDataResponse<IBaseAdmin>>({
      url: Endpoints.clientAdmin.companies.admins.root,
      version: 'v2',
      params: {
        page: filters.page,
        pageSize: filters.pageSize,
        ...getSorting(filters.sort),
        search: filters.search || undefined,
        filter: {
          adminIds: filters.admins,
          status: filters.status,
          roles: filters.role,
          companyIdsPermissionScope: filters.companies,
          ...getRoleFilters(filters.roleIds),
        },
      },
    });
  },
  getAdmin: ({ adminId }: GetAdmin) => {
    return api<IAdmin>({
      url: Endpoints.clientAdmin.companies.admins.byId.replace(
        ':adminId',
        adminId,
      ),
    });
  },
  createAdmin: (data: CreateAdmin) => {
    return api<{ id: string }>({
      url: Endpoints.clientAdmin.companies.admins.create.admin,
      data,
      method: 'post',
    });
  },
  createProgrammaticAdmin: (data: CreateProgrammaticAdmin) => {
    return api<{ id: string; apiKey: string }>({
      url: Endpoints.clientAdmin.companies.admins.create.programmatic,
      data,
      method: 'post',
    });
  },
  updateAdmin: ({ adminId, data }: UpdateAdmin) => {
    return api<TableDataResponse<IBaseAdmin>>({
      url: Endpoints.clientAdmin.companies.admins.byId.replace(
        ':adminId',
        adminId,
      ),
      data: {
        ...data,
        phone: data.phone
          ? data.phone.charAt(0) === '+'
            ? data.phone
            : `+${data.phone}`
          : undefined,
        email: data.email ? data.email.toLowerCase() : undefined,
      },
      method: 'patch',
    });
  },
  updateMe: ({ data }: UpdateMe) => {
    return api<TableDataResponse<IBaseAdmin>>({
      url: Endpoints.clientAdmin.companies.admins.me,
      data: {
        ...data,
      },
      method: 'patch',
    });
  },
  deleteAdmin: ({ adminId }: DeleteAdmin) => {
    return api({
      url: Endpoints.clientAdmin.companies.admins.delete.hr.replace(
        ':adminId',
        adminId,
      ),
      method: 'delete',
    });
  },
  sendInvite: ({ adminId }: SendInvite) => {
    return api({
      url: Endpoints.clientAdmin.companies.admins.invite.replace(
        ':adminId',
        adminId,
      ),
      method: 'post',
    });
  },
  createAdminToken: ({ adminId, data }: CreateAdminToken) => {
    return api<IAdmin['tokens'][0] & { accessToken: string }>({
      url: Endpoints.clientAdmin.companies.admins.tokens.root.replace(
        ':adminId',
        adminId,
      ),
      data,
      method: 'post',
    });
  },
  deleteAdminToken: ({ adminId, jti }: DeleteAdminToken) => {
    return api({
      url: Endpoints.clientAdmin.companies.admins.tokens.byId
        .replace(':adminId', adminId)
        .replace(':jti', jti),
      method: 'delete',
    });
  },
};
